import { useState } from "react";
import { t } from "@/i18n-js/instance";
import {
  ModalManager,
  useModal,
} from "@circle-react/components/Layout/ModalManagerProvider";
import { useTailwindSmScreenMediaQuery } from "@circle-react/hooks/useMediaQuery";
import { Button } from "@circle-react-uikit/Button";
import { Dropdown } from "@circle-react-uikit/Dropdown";
import { popperSameWidthModifier } from "@circle-react-uikit/Dropdown/utils";
import { Modal, useModalData } from "@circle-react-uikit/ModalV2";
import { Typography } from "@circle-react-uikit/Typography";

export interface ChatExportConfirmModalProps {
  onConfirm: (selectedOption: string) => void;
}

const ChatExportConfirmModalComponent = ({
  onConfirm,
}: ChatExportConfirmModalProps) => {
  const modal = useModal();
  const [selectedOption, setSelectedOption] = useState("month");
  const { contentPortalElement } = useModalData();
  const isModalView = useTailwindSmScreenMediaQuery();
  const options = [
    { label: t("settings.spaces.chat_export.option_1"), value: "month" },
    { label: t("settings.spaces.chat_export.option_2"), value: "quarter" },
    { label: t("settings.spaces.chat_export.option_3"), value: "year" },
    { label: t("settings.spaces.chat_export.option_4"), value: "all" },
  ];

  const handleConfirm = () => {
    void modal.hide();
    onConfirm(selectedOption);
  };

  const handleCancel = () => {
    void modal.hide();
  };

  return (
    <Modal isOpen={modal.visible} onClose={handleCancel}>
      <Modal.Overlay />
      <Modal.Content size="lg" className="gap-3 md:max-w-[571px]">
        <Modal.Header>
          <Modal.Title>
            <Typography.TitleMd>
              {t("settings.spaces.chat_export.confirm")}
            </Typography.TitleMd>
          </Modal.Title>
          <Modal.HeaderActions>
            <Modal.CloseButton onClick={handleCancel} />
          </Modal.HeaderActions>
        </Modal.Header>
        <Modal.Body className="!py-0 px-8" id="chat-export-confirm-modal">
          <div className="space-y-4">
            <div className="space-y-2 md:w-[507px]">
              <Typography.LabelMd weight="medium">
                {t("settings.spaces.chat_export.time_frame")}:
              </Typography.LabelMd>
              <Dropdown
                onChange={setSelectedOption}
                options={options}
                size="full"
                className="border-secondary bg-primary w-full rounded-md border"
                buttonWrapperClassName="flex flex-col w-full"
                linkType="button"
                menuWrapperClassName="!px-0"
                menuItemsClassName="!p-2"
                popperOptions={{
                  modifiers: [
                    {
                      name: "offset",
                      options: {
                        offset: [0, 4],
                      },
                    },
                    {
                      name: "zIndex",
                      enabled: isModalView,
                      phase: "write",
                      fn: ({ state }) => {
                        if (state.styles.popper) {
                          state.styles.popper.zIndex = "1100";
                        }
                      },
                    },
                    popperSameWidthModifier,
                  ],
                }}
                appendTo={
                  isModalView
                    ? (contentPortalElement ?? document.body)
                    : undefined
                }
                button={
                  <Dropdown.ActiveLabelButton
                    className="flex w-full items-center justify-between px-3"
                    options={options}
                    activeOptionValue={selectedOption}
                  />
                }
              />
              <Typography.LabelXs color="text-tertiary" weight="normal">
                {t("settings.spaces.chat_export.download_notification")}
              </Typography.LabelXs>
            </div>
          </div>
        </Modal.Body>
        <Modal.Footer className="px-8 py-4">
          <div className="flex justify-end gap-2">
            <Button
              type="button"
              variant="secondary"
              onClick={handleCancel}
              large
            >
              {t("cancel")}
            </Button>
            <Button
              type="button"
              variant="circle"
              onClick={handleConfirm}
              large
            >
              {t("confirm")}
            </Button>
          </div>
        </Modal.Footer>
      </Modal.Content>
    </Modal>
  );
};

export const ChatExportConfirmModal = ModalManager.create(
  ChatExportConfirmModalComponent,
);

export const useChatExportConfirmModal = () => useModal(ChatExportConfirmModal);
